// The composer's one button for emoji, GIFs and stickers (like Discord's): a panel with three tabs, Emoji first open.
// Each tab is the ordinary picker in its `embedded` mode; this panel places it above the message box (a bottom sheet
// on phones), closes on a click outside or Escape, and keeps one size so switching tabs doesn't jump. Opening it, or
// switching to a tab, puts the cursor in that tab's search box (on phones the keyboard stays down until you tap it).
import { useEffect, useRef } from "react";
import type { Sticker } from "@bloom/shared";
import type { CustomEmoji } from "@bloom/shared";
import { isNarrow } from "../mobile.ts";
import { Sheet } from "./Sheet.tsx";
import { EmojiPicker } from "./EmojiPicker.tsx";
import { GifPicker } from "./GifPicker.tsx";
import { StickerPicker } from "./StickerPicker.tsx";
import type { GifAttachment } from "../gifs.ts";

export type MediaTab = "gif" | "sticker" | "emoji";

const TABS: { id: MediaTab; label: string }[] = [
  { id: "gif", label: "GIFs" },
  { id: "sticker", label: "Stickers" },
  { id: "emoji", label: "Emoji" },
];

/** Marks the button that opens the panel, so pressing it while open closes the panel instead of reopening it. */
export const MEDIA_TOGGLE = "data-media-toggle";

export function MediaPicker({
  tab,
  onTab,
  onClose,
  customerId,
  onEmoji,
  onGif,
  onSticker,
  gifQuery,
}: {
  tab: MediaTab;
  onTab: (tab: MediaTab) => void;
  onClose: () => void;
  customerId: string;
  onEmoji: (emoji: string, custom?: CustomEmoji) => void;
  onGif: (gif: GifAttachment) => void;
  onSticker: (sticker: Sticker) => void;
  /** The GIF tab opens already searched for these words ("/gif cats", #632). */
  gifQuery?: string;
}) {
  const box = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  const sheet = isNarrow();

  useEffect(() => {
    if (sheet) return; // the sheet has its own backdrop, Escape and drag to close
    const onDown = (e: MouseEvent) => {
      const t = e.target as Element;
      if (box.current?.contains(t) || t.closest?.(`[${MEDIA_TOGGLE}]`)) return;
      close.current();
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close.current();
    // Next tick, so the press that opened it doesn't close it.
    const t = setTimeout(() => document.addEventListener("mousedown", onDown), 0);
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [sheet]);

  const tabs = (
    <div className="media-tabs" role="tablist" aria-label="Emoji, GIFs and stickers">
      {TABS.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          id={`media-tab-${t.id}`}
          aria-selected={tab === t.id}
          className={`media-tab${tab === t.id ? " on" : ""}`}
          // Keep focus where it is (the search box takes it on the new tab).
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onTab(t.id)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );

  // `key`: each tab mounts fresh, so its search box takes the cursor when you switch to it.
  const body =
    tab === "emoji" ? (
      <EmojiPicker key="emoji" embedded onPick={onEmoji} onClose={onClose} />
    ) : tab === "gif" ? (
      <GifPicker key="gif" embedded customerId={customerId} onPick={onGif} onClose={onClose} initialQuery={gifQuery} />
    ) : (
      <StickerPicker key="sticker" embedded onPick={onSticker} onClose={onClose} />
    );

  const panel = (
    <div className={`media-picker${sheet ? " in-sheet" : ""}`} ref={box} role="dialog" aria-label="Emoji, GIFs and stickers">
      {tabs}
      {body}
    </div>
  );
  return sheet ? (
    <Sheet label="Emoji, GIFs and stickers" onClose={onClose}>
      {panel}
    </Sheet>
  ) : (
    panel
  );
}
